'use client';

import { useState } from 'react';

import { FEATURE_FLAGS, usePosthogFeatureFlag } from '@/shared-measurement';
import { useProjectResponseModeContext } from '@/shared-survey';
import { OptionRenderType, type GetProjectQuery } from '@/survey-graphql';

import { useResponderEmail } from './project-response-insight-info.hooks';
import type { HandsFreeContentSession } from './project-usability-handsfree-content-session.hooks';
import type { HandsFreeContentVoice } from './project-usability-handsfree-content-voice.hooks';
import { useHandsFreeResponseMode } from './project-usability-handsfree-controls.hooks';
import { useOnboardingUIState } from './project-usability-handsfree-onboarding.hooks';
import {
  type HandsFreePopupLifecycle,
  useHandsFreePopupLifecycle,
} from './project-usability-handsfree-popup-lifecycle.hooks';
import {
  getHandsFreePopupSelectionValue,
  getNextHandsFreePopupSelection,
  type HandsFreePopupSelection,
} from './project-usability-handsfree.helpers';
import { useMobileAppPreloadDriver } from './project-usability-mobile-app-preload.hooks';
import {
  deriveMobileUtControlsLayout,
  shouldAwaitTaskCompletion,
} from './project-usability-mobile-controls.helpers';
import { useReportProblemSubmit } from './survey-project-response-report-problem.hooks';

interface HandsFreeContentPopupOptions
  extends
    Pick<
      HandsFreeContentSession,
      | 'messages'
      | 'isConversationOver'
      | 'replyFeedbackIds'
      | 'contentUrl'
      | 'mobileAppEmbedUrl'
      | 'insightId'
      | 'isOnboardingActive'
      | 'currentOnboardingStep'
      | 'isHelpExpanded'
      | 'isRecording'
    >,
    Pick<HandsFreeContentVoice, 'effectiveIsConversationOver'> {
  project: GetProjectQuery['project'];
  isMobileMode: boolean;
}

export interface HandsFreeContentPopup extends Pick<
  HandsFreePopupLifecycle,
  | 'currentQuestionIndex'
  | 'totalQuestions'
  | 'isPopupOpen'
  | 'togglePopup'
  | 'openPopup'
  | 'closePopup'
  | 'hidePopup'
  | 'onPopupMouseEnter'
  | 'onPopupMouseLeave'
  | 'lastAssistantMessage'
  | 'replyFeedbackMessage'
> {
  isMobileAppPreloading: boolean;
  shouldFloatControls: boolean;
  isCollapsed: boolean;
  awaitingTaskCompletion: boolean;
  isImageChoiceActive: boolean;
  popupSelectionValue: string;
  handlePopupSelectionChange: (value: string) => void;
  onReportProblemSubmit: ReturnType<typeof useReportProblemSubmit> | undefined;
  effectivePopupOpen: boolean;
  effectiveHelpExpanded: boolean;
  canCloseTaskPanel: boolean;
}

export function useHandsFreeContentPopup({
  project,
  isMobileMode,
  messages,
  isConversationOver,
  replyFeedbackIds,
  contentUrl,
  mobileAppEmbedUrl,
  insightId,
  isOnboardingActive,
  currentOnboardingStep,
  isHelpExpanded,
  isRecording,
  effectiveIsConversationOver,
}: HandsFreeContentPopupOptions): HandsFreeContentPopup {
  const responderEmail = useResponderEmail();

  const [popupSelection, setPopupSelection] = useState<HandsFreePopupSelection>(
    { questionId: null, value: '' }
  );

  const { responseMode, setResponseMode } = useProjectResponseModeContext();

  const {
    currentQuestionIndex,
    totalQuestions,
    isPopupOpen,
    togglePopup,
    openPopup,
    closePopup,
    hidePopup,
    onPopupMouseEnter,
    onPopupMouseLeave,
    lastAssistantMessage,
    replyFeedbackMessage,
    hasClosedTaskBar,
  } = useHandsFreePopupLifecycle({
    questions: project.questions,
    messages,
    isConversationOver,
    replyFeedbackIds,
  });

  const { isPreloading: isMobileAppPreloading } = useMobileAppPreloadDriver({
    questions: project.questions,
    currentQuestionIndex,
  });

  const { shouldFloatControls, isCollapsed } = deriveMobileUtControlsLayout({
    isMobileMode,
    contentUrl,
    mobileAppEmbedUrl,
    isMobileAppPreloading,
  });

  const awaitingTaskCompletion = shouldAwaitTaskCompletion({
    isMobileMode,
    questionType: lastAssistantMessage?.type,
    hasClosedTaskBar,
  });

  const isImageChoiceActive = Boolean(
    lastAssistantMessage &&
    lastAssistantMessage.type?.toLowerCase() === 'multiplechoice' &&
    lastAssistantMessage.optionRenderType === OptionRenderType.Media &&
    !effectiveIsConversationOver
  );

  const activeQuestionId = lastAssistantMessage?.questionId ?? null;
  const nextPopupSelection = getNextHandsFreePopupSelection(
    popupSelection,
    activeQuestionId
  );
  if (nextPopupSelection !== popupSelection) {
    setPopupSelection(nextPopupSelection);
  }
  const popupSelectionValue = getHandsFreePopupSelectionValue(
    popupSelection,
    activeQuestionId
  );
  const handlePopupSelectionChange = (value: string) => {
    setPopupSelection({ questionId: activeQuestionId, value });
  };

  const { flag: reportProblemFlag } = usePosthogFeatureFlag(
    FEATURE_FLAGS.REPORT_PROBLEM_FEATURE
  );
  const handleReportProblemSubmit = useReportProblemSubmit({
    project,
    insightId,
    responderEmail,
  });

  const { effectivePopupOpen, effectiveHelpExpanded, canCloseTaskPanel } =
    useOnboardingUIState({
      isOnboardingActive,
      currentOnboardingStep,
      isPopupOpen,
      isHelpExpanded,
    });

  useHandsFreeResponseMode({
    isRecording,
    isOnboardingActive,
    responseMode,
    setResponseMode,
  });

  return {
    currentQuestionIndex,
    totalQuestions,
    isPopupOpen,
    togglePopup,
    openPopup,
    closePopup,
    hidePopup,
    onPopupMouseEnter,
    onPopupMouseLeave,
    lastAssistantMessage,
    replyFeedbackMessage,
    isMobileAppPreloading,
    shouldFloatControls,
    isCollapsed,
    awaitingTaskCompletion,
    isImageChoiceActive,
    popupSelectionValue,
    handlePopupSelectionChange,
    onReportProblemSubmit:
      reportProblemFlag(handleReportProblemSubmit) ?? undefined,
    effectivePopupOpen,
    effectiveHelpExpanded,
    canCloseTaskPanel,
  };
}
